import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "./shell/NotificationBell";
import { ProfileMenu } from "./shell/ProfileMenu";
import { CommandPalette } from "./shell/CommandPalette";
import SystemStatusPill from "./SystemStatusPill";
import { useAuth } from "@/context/AuthContext";
import { TID } from "../lib/testIds";

/** Sticky nav — one-page-two-doors layout.
 * Primary nav is now audience-first (For Employers · For Talent · Pricing) so
 * a visitor picks a door in under 2 seconds. Secondary discovery lives on the
 * respective landing pages, not in the header. */
const links = [
    { to: "/employers", label: "For Employers", id: TID.nav.employers },
    { to: "/talent", label: "For Talent", id: TID.nav.talent },
    { to: "/pricing", label: "Pricing", id: TID.nav.pricing },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { user } = useAuth();

    React.useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header className="sticky top-0 z-50 glass-nav" data-testid="site-navbar">
            <div className="container-page flex items-center justify-between h-20 py-2">
                <div className="shrink-0 flex items-center gap-3">
                    <Link to="/" data-testid={TID.nav.logo} aria-label="Simonara home">
                        <Logo size="lg" />
                    </Link>
                    <SystemStatusPill variant="navbar" />
                </div>

                <nav className="hidden lg:flex items-center gap-1" aria-label="Primary navigation">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={l.id}
                            className={({ isActive }) =>
                                `px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                                    isActive
                                        ? "bg-brand-navy text-white dark:bg-slate-800"
                                        : "text-foreground hover:text-brand-navy hover:bg-accent dark:hover:text-emerald-400"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-2">
                    <CommandPalette />
                    <ThemeToggle />
                    {user ? (
                        <>
                            <NotificationBell />
                            <ProfileMenu />
                        </>
                    ) : (
                        <>
                            <Link
                                to="/login"
                                data-testid="nav-signin"
                                className="px-4 py-2 rounded-full text-sm font-medium text-foreground hover:bg-accent transition-colors"
                            >
                                Sign in
                            </Link>
                            <Link
                                to="/talent/signup"
                                data-testid="nav-cta-find-work"
                                className="btn-outline text-sm py-2.5 px-4"
                            >
                                Find work
                            </Link>
                            <Link
                                to="/employers/signup"
                                data-testid="nav-cta-post-job"
                                className="btn-green text-sm py-2.5 px-5"
                            >
                                Post a job
                            </Link>
                        </>
                    )}
                </div>

                <div className="lg:hidden flex items-center gap-2">
                    <ThemeToggle />
                    <button
                        className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-border text-foreground"
                        aria-label="Toggle menu"
                        data-testid={TID.nav.mobileToggle}
                        onClick={() => setOpen((v) => !v)}
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden border-t border-border bg-background">
                    <div className="container-page py-4 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`${l.id}-mobile`}
                                className={({ isActive }) =>
                                    `px-4 py-3 rounded-xl text-base font-medium ${
                                        isActive ? "bg-brand-navy text-white dark:bg-slate-800" : "text-foreground hover:bg-accent"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {user ? (
                            <Link to="/dashboard" className="btn-green mt-2 w-full text-center">Dashboard</Link>
                        ) : (
                            <>
                                <Link
                                    to="/login"
                                    data-testid="nav-signin-mobile"
                                    className="px-4 py-3 rounded-xl text-base font-medium text-foreground hover:bg-accent"
                                >
                                    Sign in
                                </Link>
                                <div className="mt-2 grid grid-cols-2 gap-2">
                                    <Link
                                        to="/talent/signup"
                                        data-testid="nav-cta-find-work-mobile"
                                        className="btn-outline text-center"
                                    >
                                        Find work
                                    </Link>
                                    <Link
                                        to="/employers/signup"
                                        data-testid="nav-cta-post-job-mobile"
                                        className="btn-green text-center"
                                    >
                                        Post a job
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
